/**
 * NVR / DVR per-model page helpers — slug generation, lookup, related units.
 *
 * Mirrors `cameraSlug.ts`. Only entries marked `verified: true` AND with a
 * `datasheetUrl` get a public page. Unverified entries stay in the designer
 * catalog but don't get an indexable URL — anti-hallucination is the
 * first-class concern: a public page must be traceable to a primary
 * datasheet, not an inferred spec.
 *
 * As of 2026-05-20 the audit shows 663 verified+datasheet NVR entries out
 * of 4,008 total. The remaining ~3,345 are flagged for next research pass.
 */
import {
  EQUIPMENT_CATALOG,
  type NVRSpec,
  type EquipmentCatalogEntry,
} from '@/components/cctv-designer-v2/equipmentCatalog';
import { getAllCamerasForStaticPages, getMegapixels, type CameraPageData } from './cameraSlug';
import { storageGbPerDay } from './storageMath';

/** Narrow EquipmentCatalogEntry union to NVR with both verified + datasheet. */
export interface NVRPageData {
  id: string;
  manufacturer: string;
  model: string;
  slug: string;
  tagline?: string;
  datasheetUrl: string;
  specs: NVRSpec;
}

function isPublishableNvr(e: EquipmentCatalogEntry): e is EquipmentCatalogEntry & { category: 'nvr-recorder'; specs: NVRSpec } {
  return (
    e.category === 'nvr-recorder' &&
    !e.hidden &&
    (e as { verified?: boolean }).verified === true &&
    !!(e as { datasheetUrl?: string }).datasheetUrl
  );
}

/**
 * Slug generator. Drops manufacturer prefix when the model already contains
 * the brand (e.g. "Dahua NVR5216" → already collides — keeps deterministic
 * `manufacturer-model` form, normalised). Same algorithm as cameraSlug for
 * predictability of URL shape across the catalog.
 */
export function generateNvrSlug(manufacturer: string, model: string): string {
  return `${manufacturer}-${model}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

let _cachedNvrs: NVRPageData[] | null = null;

/** Build the publishable NVR set once and cache. Skips duplicates by slug —
 *  multiple catalog files can describe the same SKU when families overlap. */
export function getAllPublishableNvrs(): NVRPageData[] {
  if (_cachedNvrs) return _cachedNvrs;

  const seen = new Set<string>();
  const out: NVRPageData[] = [];

  for (const e of EQUIPMENT_CATALOG) {
    if (!isPublishableNvr(e)) continue;
    const slug = generateNvrSlug(e.manufacturer, e.model);
    if (seen.has(slug)) continue;
    seen.add(slug);
    out.push({
      id: e.id,
      manufacturer: e.manufacturer,
      model: e.model,
      slug,
      tagline: (e as { tagline?: string }).tagline,
      datasheetUrl: (e as { datasheetUrl: string }).datasheetUrl,
      specs: e.specs,
    });
  }

  _cachedNvrs = out;
  return out;
}

/** Slugs only — fed to SSG and sitemap generator. Same SKIP escape hatch as
 *  cameras so hotfix builds can skip the per-NVR SSG cost. */
export function getAllNvrSlugs(): string[] {
  if (typeof process !== 'undefined' && process.env?.SKIP_CAMERA_PAGES === '1') {
    return [];
  }
  return getAllPublishableNvrs().map(n => `nvr/${n.slug}`);
}

let _slugMap: Map<string, NVRPageData> | null = null;
function slugMap(): Map<string, NVRPageData> {
  if (!_slugMap) {
    _slugMap = new Map();
    for (const n of getAllPublishableNvrs()) _slugMap.set(n.slug, n);
  }
  return _slugMap;
}

export function getNvrBySlug(slug: string): NVRPageData | undefined {
  return slugMap().get(slug);
}

/** One manufacturer's recorders, for the `/nvr` index. */
export interface NvrBrandGroup {
  manufacturer: string;
  nvrs: NVRPageData[];
}

/**
 * Every publishable recorder grouped by manufacturer, biggest catalogue first.
 *
 * Feeds the `/nvr` hub, which exists for the same reason the brand hubs do.
 * Measured 2026-08-17: all 693 `/nvr/*` pages were link orphans — zero inbound
 * anchors from `/`, `/cameras`, `/features`, `/pricing`, `/blog`, `/compare` or
 * anywhere else browsable. Each one links ten same-brand siblings, so they form
 * closed islands, and each links out to `/camera/*` pages, so they hand their
 * equity downstream while receiving none. The camera catalog had exactly this
 * shape until brand hubs fixed it; recorders never got the same treatment.
 *
 * Grouped rather than paginated on purpose: 693 models is small enough for one
 * document, and `/nvr/2` would collide with the `nvr/:slug` detail route.
 * Ordering is deterministic (count, then name, then model) so the prerendered
 * HTML is byte-identical between builds.
 */
export function getNvrsByBrand(): NvrBrandGroup[] {
  const byBrand = new Map<string, NVRPageData[]>();
  for (const n of getAllPublishableNvrs()) {
    const list = byBrand.get(n.manufacturer);
    if (list) list.push(n);
    else byBrand.set(n.manufacturer, [n]);
  }
  return [...byBrand.entries()]
    .map(([manufacturer, nvrs]) => ({
      manufacturer,
      nvrs: [...nvrs].sort((a, b) => a.model.localeCompare(b.model)),
    }))
    .sort((a, b) => b.nvrs.length - a.nvrs.length || a.manufacturer.localeCompare(b.manufacturer));
}

/** Related NVRs — same manufacturer, ranked by channel-count proximity so
 *  the upsell ladder (8-ch → 16-ch → 32-ch) makes operational sense. */
export function getRelatedNvrs(nvr: NVRPageData, max = 6): NVRPageData[] {
  const all = getAllPublishableNvrs();
  const here = nvr.specs.channels;
  return all
    .filter(n => n.slug !== nvr.slug && n.manufacturer === nvr.manufacturer)
    .sort((a, b) => Math.abs(a.specs.channels - here) - Math.abs(b.specs.channels - here))
    .slice(0, max);
}

/** Cross-sell — different brand, similar channel-count. Useful for "compare
 *  alternatives" without bias toward one vendor. */
export function getCrossSellNvrs(nvr: NVRPageData, max = 4): NVRPageData[] {
  const all = getAllPublishableNvrs();
  const here = nvr.specs.channels;
  return all
    .filter(n => n.manufacturer !== nvr.manufacturer && Math.abs(n.specs.channels - here) <= 8)
    .sort((a, b) => Math.abs(a.specs.channels - here) - Math.abs(b.specs.channels - here))
    .slice(0, max);
}

/** Storage planning math used on every NVR page. Returns daily + 30-day
 *  storage for a typical install — pure function, deterministic, no LLM
 *  hallucinations. Bitrate assumed = 4 Mbps / channel (industry typical
 *  for 4 MP H.265 CBR at 15 fps; documented as such in our DORI / DORI
 *  calculator). Callers can substitute their own bitrate. */
export function estimateStorageGb(channels: number, retentionDays: number, bitrateMbps = 4): number {
  // Decimal GB, matching how drives and NVR datasheets quote capacity: 10.8
  // GB/day per channel at 1 Mbps. See lib/storageMath for why not 10.55.
  const gbPerChannelPerDay = storageGbPerDay(bitrateMbps);
  return Math.round(channels * gbPerChannelPerDay * retentionDays);
}

/**
 * Parse the recorder's max per-channel resolution into a megapixel ceiling.
 * Handles '12 MP', '8 MP', '4K', '8K'; falls back to 8 MP (the modern default)
 * when the spec is absent or unparseable. Used to pick compatible cameras.
 */
export function getNvrMaxMegapixels(nvr: NVRPageData): number {
  const r = nvr.specs.maxResolution;
  if (!r) return 8;
  const mp = r.match(/(\d+(?:\.\d+)?)\s*MP/i);
  if (mp) return parseFloat(mp[1]);
  if (/8\s*K/i.test(r)) return 33;
  if (/4\s*K/i.test(r)) return 8;
  return 8;
}

let _cameraPoolByMp: CameraPageData[] | null = null;
function cameraPoolByMp(): CameraPageData[] {
  if (!_cameraPoolByMp) {
    _cameraPoolByMp = getAllCamerasForStaticPages()
      .filter(c => getMegapixels(c.resolution) > 0)
      .sort((a, b) => getMegapixels(b.resolution) - getMegapixels(a.resolution));
  }
  return _cameraPoolByMp;
}

/**
 * "Compatible cameras for {NVR}" — deterministic pick of catalog cameras that
 * fit the recorder's per-channel resolution ceiling. Same-manufacturer cameras
 * first (natural pairing), then one camera per other brand for ONVIF variety.
 * Creates bidirectional internal-link equity between the /nvr/ and /camera/
 * trees. Returns [] if the camera set is skipped (build:fast).
 */
export function getRecommendedCameras(nvr: NVRPageData, max = 6): CameraPageData[] {
  const maxMp = getNvrMaxMegapixels(nvr);
  const pool = cameraPoolByMp().filter(c => getMegapixels(c.resolution) <= maxMp + 0.5);
  const brand = nvr.manufacturer.toLowerCase();
  const picks: CameraPageData[] = pool.filter(c => c.manufacturer.toLowerCase() === brand).slice(0, max);
  if (picks.length < max) {
    for (const c of pool) {
      if (picks.length >= max) break;
      if (c.manufacturer.toLowerCase() === brand) continue;
      if (picks.some(p => p.manufacturer.toLowerCase() === c.manufacturer.toLowerCase())) continue;
      picks.push(c);
    }
  }
  return picks.slice(0, max);
}
